import React, { useState } from "react";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, Brain, Loader2, Sparkles, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import FollowUpFlow from "@/components/app/FollowUpFlow";
import BookingPayment from "@/components/app/BookingPayment";

export default function FirstTimeFlow({ patient, onReset, onBack }) {
  const { t, lang } = useLang();
  const [mode, setMode] = useState(null); // disease | symptoms
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [pickedDept, setPickedDept] = useState(null);
  const [pickedDoctor, setPickedDoctor] = useState(null);

  const runTriage = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const data = await api.triage({ mode, text, language: lang });
      setResults(data);
    } catch (e) {
      toast.error("AI triage failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (pickedDoctor) {
    return (
      <BookingPayment
        patient={patient}
        doctor={pickedDoctor}
        department={pickedDept}
        consultationType="first_time"
        disease={text}
        onReset={onReset}
        onBack={() => setPickedDoctor(null)}
      />
    );
  }

  if (pickedDept) {
    return (
      <DoctorPicker
        departmentId={pickedDept}
        onBack={() => setPickedDept(null)}
        onPick={setPickedDoctor}
      />
    );
  }

  if (!mode) {
    return (
      <div className="mx-auto max-w-4xl py-6">
        <Button variant="ghost" onClick={onBack} data-testid="back-btn" className="mb-4">
          <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("back")}
        </Button>
        <h2 className="mb-6 text-2xl font-bold tracking-tight sm:text-3xl">{t("ai_triage_title")}</h2>
        <div className="grid gap-5 md:grid-cols-2">
          <ChoiceCard icon={Stethoscope} title={t("know_disease")} desc={t("know_disease_desc")} onClick={() => setMode("disease")} testId="choice-disease" color="from-sky-500 to-indigo-500" />
          <ChoiceCard icon={Brain} title={t("describe_symptoms")} desc={t("describe_symptoms_desc")} onClick={() => setMode("symptoms")} testId="choice-symptoms" color="from-teal-500 to-emerald-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl py-6">
      <Button variant="ghost" onClick={() => { setMode(null); setResults(null); setText(""); }} data-testid="back-btn" className="mb-4">
        <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("back")}
      </Button>

      <Card className="border-slate-200 bg-white p-6 sm:p-8">
        <div className="mb-4 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-sky-600" />
          <h2 className="text-xl font-bold sm:text-2xl">{t("ai_triage_title")}</h2>
        </div>
        {mode === "disease" ? (
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("disease_placeholder")}
            data-testid="triage-disease-input"
            className="h-12"
          />
        ) : (
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("symptoms_placeholder")}
            rows={4}
            data-testid="triage-symptoms-input"
          />
        )}
        <Button
          disabled={!text.trim() || loading}
          onClick={runTriage}
          data-testid="ai-triage-submit"
          className="mt-4 h-12 w-full bg-sky-600 hover:bg-sky-700 sm:w-auto"
        >
          {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("analyzing")}</> : <><Sparkles className="mr-2 h-4 w-4" /> {t("analyze")}</>}
        </Button>
      </Card>

      {results && (
        <div className="mt-6">
          <h3 className="mb-4 text-lg font-bold tracking-tight sm:text-xl">{t("recommendations")}</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="recommendation-grid">
            {results.recommendations.map((r) => {
              const tier = r.score >= 80 ? "high" : r.score >= 50 ? "medium" : r.score >= 1 ? "low" : "zero";
              const styles = tier === "high"
                ? "border-emerald-300 bg-emerald-50"
                : tier === "medium"
                ? "border-sky-300 bg-sky-50"
                : tier === "low"
                ? "border-amber-300 bg-amber-50"
                : "border-slate-200 bg-slate-50";
              const progressColor = tier === "high" ? "bg-emerald-500" : tier === "medium" ? "bg-sky-500" : tier === "low" ? "bg-amber-500" : "bg-slate-400";
              const label = tier === "high" ? t("highly_recommended") : tier === "medium" ? t("recommended") : tier === "low" ? t("possible") : t("unrelated");
              return (
                <Card key={r.department_id} className={`border p-4 ${styles}`} data-testid={`rec-${r.department_id}`}>
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <h4 className="font-bold text-slate-900">{r.department_name}</h4>
                    <Badge variant="outline" className="shrink-0 border-slate-300 bg-white text-slate-700">{r.score}%</Badge>
                  </div>
                  <div className="mb-3 h-2 overflow-hidden rounded-full bg-white/60">
                    <div className={`h-full ${progressColor} transition-all`} style={{ width: `${r.score}%` }} />
                  </div>
                  <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</div>
                  <Button
                    onClick={() => setPickedDept(r.department_id)}
                    data-testid={`select-dept-${r.department_id}-btn`}
                    size="sm"
                    className="w-full bg-slate-900 hover:bg-slate-800"
                  >
                    Select {r.department_name} <ArrowRight className="ml-1 h-3.5 w-3.5" />
                  </Button>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ChoiceCard({ icon: Icon, title, desc, onClick, testId, color }) {
  return (
    <button onClick={onClick} data-testid={testId} className="group text-left transition hover:-translate-y-1">
      <Card className="h-full border-slate-200 bg-white p-6 transition hover:shadow-xl">
        <div className={`mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${color} text-white shadow-md`}>
          <Icon className="h-7 w-7" />
        </div>
        <h3 className="mb-2 text-lg font-bold">{title}</h3>
        <p className="text-sm leading-relaxed text-slate-600">{desc}</p>
      </Card>
    </button>
  );
}

function DoctorPicker({ departmentId, onBack, onPick }) {
  const { t } = useLang();
  const [doctors, setDoctors] = React.useState([]);
  React.useEffect(() => {
    api.doctors(departmentId).then(setDoctors);
  }, [departmentId]);
  return (
    <div className="mx-auto max-w-5xl py-6">
      <Button variant="ghost" onClick={onBack} data-testid="back-btn" className="mb-4">
        <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("back")}
      </Button>
      <h2 className="mb-6 text-2xl font-bold tracking-tight sm:text-3xl">{t("select_doctor")}</h2>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {doctors.map((d) => (
          <Card key={d.id} className="border-slate-200 bg-white p-5" data-testid={`doctor-card-${d.id}`}>
            <div className="flex items-center gap-4">
              {d.photo ? (
                <img src={d.photo} alt="" className="h-14 w-14 rounded-full object-cover ring-2 ring-sky-100" />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-sky-100 text-sky-700 font-bold">
                  {d.name.split(" ").slice(-1)[0][0]}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h3 className="font-bold">{d.name}</h3>
                <div className="text-sm text-slate-500">₹{d.fee}</div>
              </div>
            </div>
            <Button onClick={() => onPick(d)} className="mt-4 w-full bg-sky-600 hover:bg-sky-700" data-testid={`pick-doctor-${d.id}-btn`}>
              {t("select_slot")}
            </Button>
          </Card>
        ))}
        {doctors.length === 0 && (
          <Card className="col-span-full p-8 text-center text-slate-500">No doctors in this department yet.</Card>
        )}
      </div>
    </div>
  );
}
